import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, Star, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const emptyProd = { name: '', description: '', price: '', category_id: '', image_url: '', tags: '', available: true, is_signature: false };
const emptyCat = { name: '', icon: '', order: '' };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prodOpen, setProdOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [form, setForm] = useState(emptyProd);
  const [catForm, setCatForm] = useState(emptyCat);
  const [editId, setEditId] = useState(null);

  const load = () => Promise.all([api.get('/admin/products'), api.get('/categories')])
    .then(([p, c]) => { setProducts(p.data); setCategories(c.data); }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNewProd = () => { setForm(emptyProd); setEditId(null); setProdOpen(true); };
  const openEditProd = (p) => {
    setForm({ ...p, tags: (p.tags || []).join(', '), price: p.price.toString() });
    setEditId(p.product_id); setProdOpen(true);
  };

  const saveProd = async () => {
    const payload = { ...form, price: parseFloat(form.price), tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    try {
      if (editId) { await api.put(`/admin/products/${editId}`, payload); toast.success('Produit mis a jour'); }
      else { await api.post('/admin/products', payload); toast.success('Produit cree'); }
      setProdOpen(false); load();
    } catch { toast.error('Erreur'); }
  };

  const deleteProd = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/admin/products/${id}`); toast.success('Supprime'); load(); } catch { toast.error('Erreur'); }
  };

  const saveCat = async () => {
    try {
      await api.post('/admin/categories', { ...catForm, order: parseInt(catForm.order) || 0 });
      toast.success('Categorie creee'); setCatOpen(false); load();
    } catch { toast.error('Erreur'); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Supprimer cette categorie ?')) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success('Supprime'); load(); } catch { toast.error('Erreur'); }
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: typeof e === 'object' && e.target ? e.target.value : e }));

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Catalogue</h1>
        <div className="flex gap-2">
          <Button onClick={() => setCatOpen(true)} variant="outline" data-testid="admin-create-category-btn"
            className="border-white/10 rounded-full px-4 text-sm"><FolderPlus className="w-4 h-4 mr-1" /> Categorie</Button>
          <Button onClick={openNewProd} data-testid="admin-create-product-btn"
            className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-5"><Plus className="w-4 h-4 mr-1" /> Produit</Button>
        </div>
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map(c => (
          <div key={c.category_id} className="flex items-center gap-2 bg-[#121212] border border-white/5 rounded-full px-3 py-1.5">
            <span className="text-xs">{c.name}</span>
            <button onClick={() => deleteCat(c.category_id)} className="text-red-400 hover:text-red-300" data-testid={`delete-cat-${c.category_id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Products */}
      <div className="space-y-3">
        {products.map(p => {
          const cat = categories.find(c => c.category_id === p.category_id);
          return (
            <div key={p.product_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-product-${p.product_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{p.name}</span>
                  {p.is_signature && <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] text-[10px]"><Star className="w-2.5 h-2.5 mr-0.5" /> Signature</Badge>}
                  {!p.available && <Badge className="bg-red-500/15 text-red-400 text-[10px]">Indisponible</Badge>}
                </div>
                <div className="text-xs text-[#A1A1AA]">{cat?.name || 'Sans categorie'} - {p.price.toFixed(2)} EUR</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => openEditProd(p)} className="h-8 w-8 text-[#A1A1AA] hover:text-white" data-testid={`edit-product-${p.product_id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => deleteProd(p.product_id)} className="h-8 w-8 text-red-400 hover:text-red-300" data-testid={`delete-product-${p.product_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
        {products.length === 0 && <div className="text-center py-16 text-[#A1A1AA]">Aucun produit</div>}
      </div>

      {/* Product Dialog */}
      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{editId ? 'Modifier' : 'Creer'} un produit</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Nom *</Label>
              <Input value={form.name} onChange={set('name')} data-testid="product-form-name" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Description</Label>
              <Input value={form.description} onChange={set('description')} data-testid="product-form-desc" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A1A1AA]">Prix (EUR) *</Label>
                <Input type="number" step="0.01" value={form.price} onChange={set('price')} data-testid="product-form-price" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Categorie *</Label>
                <Select value={form.category_id} onValueChange={set('category_id')}>
                  <SelectTrigger className="bg-[#121212] border-white/10 rounded-xl mt-1" data-testid="product-form-category"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.available} onCheckedChange={v => setForm(p => ({ ...p, available: v }))} data-testid="product-form-available" />
                <Label className="text-xs">Disponible</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_signature} onCheckedChange={v => setForm(p => ({ ...p, is_signature: v }))} data-testid="product-form-signature" />
                <Label className="text-xs">Signature</Label>
              </div>
            </div>
            <Button onClick={saveProd} data-testid="product-form-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">
              {editId ? 'Mettre a jour' : 'Creer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Nouvelle categorie</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Nom *</Label>
              <Input value={catForm.name} onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))}
                data-testid="category-form-name" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Ordre</Label>
              <Input type="number" value={catForm.order} onChange={e => setCatForm(p => ({ ...p, order: e.target.value }))}
                data-testid="category-form-order" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <Button onClick={saveCat} data-testid="category-form-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">Creer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
